import _ from "lodash";
/** external */
import React, { memo, useState } from "react";
import PropTypes from "prop-types";
import { TextInput, IconButton } from "@toddle-edu/ds-web";
import { PencilLineOutlined, DeleteOutlined } from "@toddle-edu/ds-icons";
import { connect } from "react-redux";
import { useI18n } from "Hooks";

/** internal */
import { useDebouncedCallback } from "Utils/CustomHooks";
import { isMobileOrTablet } from "Utils/DeviceDetectHelpers";
import { updateSection } from "Worksheet/modules/WorksheetModules";
import {
  UPDATE_DEBOUNCE_CALL_TIME,
  WORKSHEET_MODES,
  MODE_TEACHER_VIEW,
  MODE_TEACHER_CREATE,
  MODE_TEACHER_EVALUATE,
  MODE_STUDENT_EVALUATE,
} from "Worksheet/modules/WorksheetConstants";
import { updateSectionLabelInCache } from "Worksheet/modules/WorksheetUtils";
import DeleteSectionModal from "Worksheet/components/DeleteSectionModal";
/** relative */
import classes from "./SectionHeader.scss";
import classNames from "classnames";

const styles = {
  textInputStyle: {
    border: "none",
    height: "22px",
    padding: "0",
  },
};

const SectionHeader = memo(props => {
  const {
    id,
    label,
    mode,
    updateSection,
    isFromLibrary,
    sectionScore,
    sectionMarks,
    isStudentRevamp,
  } = props;
  const { t } = useI18n(["common", "worksheet"]);

  const [isInputMode, setIsInputMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  /** whether header text is editable */
  const isEditable = _.isEqual(mode, MODE_TEACHER_CREATE);

  const editLabel = () => {
    if (isEditable) {
      setIsInputMode(true);
    }
  };

  const [debouncedUpdateSectionMutation] = useDebouncedCallback(
    updateSection,
    UPDATE_DEBOUNCE_CALL_TIME
  );

  const updateSectionHeader = e => {
    const newLabel = _.get(e, "target.value", "");

    updateSectionLabelInCache({ id, label: newLabel });

    debouncedUpdateSectionMutation({
      id,
      label: newLabel,
    });
  };

  const toggleDeleteDialog = () => {
    setShowDeleteDialog(prev => !prev);
  };

  const sectionHeaderContainerClass = classNames({
    [classes.container]: !isFromLibrary,
    [classes.createModeContainer]: isEditable,
    [classes.containerImportFromLibrary]: isFromLibrary,
    [classes.revampUI]: isStudentRevamp,
  });

  const labelClass = classNames(classes.label, {
    [classes.labelPadding]: isFromLibrary,
    [classes.labelBlack]: isStudentRevamp,
  });

  return (
    <div className={sectionHeaderContainerClass}>
      {isInputMode ? (
        <div className={classes.input}>
          <TextInput
            size="medium"
            value={label}
            onChange={updateSectionHeader}
            autoFocus
            onBlur={() => setIsInputMode(false)}
            style={styles.textInputStyle}
            testId="worksheet-sectionHeader-updateSectionHeader"
          />
        </div>
      ) : (
        <div className={classes.innerContainer}>
          <div className={labelClass}>{label}</div>
          {isEditable ? (
            <div className={classes.editActionsContainer}>
              <IconButton
                dsVersion="2.0"
                type="plain"
                variant="neutral"
                size="x-small"
                _legacy_size="xx-small"
                _legacy_variant="plain"
                icon={<PencilLineOutlined />}
                onClick={editLabel}
                testId="worksheet-sectionHeader-updateLabel"
              />
              <IconButton
                dsVersion="2.0"
                type="plain"
                variant="neutral"
                size="x-small"
                _legacy_size="xx-small"
                _legacy_variant="plain"
                icon={<DeleteOutlined />}
                onClick={toggleDeleteDialog}
                testId="worksheet-sectionHeader-deleteLabel"
              />
            </div>
          ) : null}
        </div>
      )}
      {showDeleteDialog ? (
        <DeleteSectionModal
          id={id}
          label={label}
          toggleModal={toggleDeleteDialog}
          mode={mode}
        />
      ) : null}
      {!isMobileOrTablet() ? (
        <div className={classes.sectionScore}>
          {_.includes(
            [MODE_STUDENT_EVALUATE, MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
            mode
          ) ? (
            <div>
              <span className="heading-5">{sectionMarks}</span>
              <span className="text-body-2">
                /{t("common:mark_with_number", { count: sectionScore })}
              </span>
            </div>
          ) : (
            <div>{t("common:mark_with_number", { count: sectionScore })}</div>
          )}
        </div>
      ) : null}
    </div>
  );
});

const mapActionCreators = {
  updateSection,
};

SectionHeader.propTypes = {
  label: PropTypes.string,
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  isFromLibrary: PropTypes.bool,
  sectionScore: PropTypes.number,
  sectionMarks: PropTypes.number,
};

SectionHeader.defaultProps = {
  label: "",
  mode: MODE_TEACHER_VIEW,
  sectionScore: 0,
  sectionMarks: 0,
};

SectionHeader.displayName = "SectionHeader";

export default connect(null, mapActionCreators)(SectionHeader);
